/**
 * TaskDetailModal - タスク詳細・編集モーダル
 *
 * ガントチャート、カレンダーなどから共通で使用するタスク詳細モーダル
 * 担当者、進捗、画像、ファイルなども含む完全な編集機能を提供
 */

import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X, Image, File, FileText, Film, Music, Archive, Eye, Download, ChevronLeft, ChevronRight, UserPlus, TrendingUp } from 'lucide-react'
import { useStore } from '../store/useStore'
import type { Todo, Project } from '../lib/supabase'
import { getUserColorFromId, getUserInitial } from '../utils/userColor'
import { AssignTaskModal } from './team/AssignTaskModal'
import { TaskProgressPanel } from './team/TaskProgressPanel'
import { getTodoImages, getThumbnailUrl, getOptimizedImageUrl, type TodoImage } from '../lib/imageStorage'
import { getTodoFiles, getFileUrl, downloadFile, type TodoFile } from '../lib/fileStorage'
import TodoImageModal from './TodoImageModal'
import TodoFileModal from './TodoFileModal'
import { useFeedbackSettings } from '../hooks/useFeedbackSettings'
import { triggerFeedback, initializeFeedback } from '../lib/feedback'
import ConfirmDialog from './ConfirmDialog'
import RichTextEditor from './RichTextEditor'

// Button SVG images
const ButtonF = '/img/buttons/ButtonF.svg' // ゴミ箱に移動
const ButtonL = '/img/buttons/ButtonL.svg' // タスクを複製

interface ImageWithUrl extends TodoImage {
  thumbnailUrl?: string
  optimizedUrl?: string
}

// File category icon helper
const getCategoryIcon = (category: TodoFile['file_category']) => {
  switch (category) {
    case 'document':
      return <FileText className="h-4 w-4 text-blue-500" />
    case 'video':
      return <Film className="h-4 w-4 text-purple-500" />
    case 'audio':
      return <Music className="h-4 w-4 text-green-500" />
    case 'archive':
      return <Archive className="h-4 w-4 text-orange-500" />
    default:
      return <File className="h-4 w-4 text-gray-500" />
  }
}

// Format file size helper
const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return '不明'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// Check if file can be previewed
const canPreview = (file: TodoFile): boolean => {
  const fileType = file.file_type || ''
  if (fileType.startsWith('image/')) return true
  if (fileType === 'application/pdf') return true
  if (fileType === 'text/plain' || fileType === 'text/csv') return true
  const officeTypes = [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  ]
  if (officeTypes.includes(fileType)) return true
  return false
}

// Get status color
const getStatusColor = (status: Todo['status']) => {
  switch (status) {
    case 'pending': return 'bg-yellow-100 text-yellow-800'
    case 'in_progress': return 'bg-blue-100 text-blue-800'
    case 'completed': return 'bg-gray-200 text-gray-600'
    case 'deleted': return 'bg-red-100 text-red-800'
    default: return 'bg-gray-100 text-gray-800'
  }
}

interface TaskDetailModalProps {
  task: Todo | null
  onClose: () => void
  getProject: (projectId: string) => Project | undefined
}

export default function TaskDetailModal({ task, onClose, getProject }: TaskDetailModalProps) {
  const { updateTodo, addTodo } = useStore()
  const { soundEnabled } = useFeedbackSettings()

  // モーダル用ステート
  const [showImageModal, setShowImageModal] = useState(false)
  const [showFileModal, setShowFileModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [showProgressPanel, setShowProgressPanel] = useState(false)

  // 添付ファイル用ステート
  const [taskImages, setTaskImages] = useState<ImageWithUrl[]>([])
  const [taskFiles, setTaskFiles] = useState<TodoFile[]>([])
  const [loadingAttachments, setLoadingAttachments] = useState(false)
  const [previewImageIndex, setPreviewImageIndex] = useState<number | null>(null)

  // 編集フォーム用ステート
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editStatus, setEditStatus] = useState<Todo['status']>('pending')
  const [editStartDate, setEditStartDate] = useState('')
  const [editDueDate, setEditDueDate] = useState('')
  const [editDueTime, setEditDueTime] = useState('')
  const [editProgress, setEditProgress] = useState(0)
  const [editAssignedUserId, setEditAssignedUserId] = useState<string | null>(null)
  const [editAssignedUser, setEditAssignedUser] = useState<Todo['assigned_user'] | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  // 編集フォーム用refs
  const editStartDateRef = useRef<HTMLInputElement>(null)
  const editDueDateRef = useRef<HTMLInputElement>(null)
  const editDueTimeRef = useRef<HTMLInputElement>(null)

  // タスクが変更されたらフォームを初期化し、添付ファイルを読み込む
  useEffect(() => {
    if (!task) return

    // 編集フォームを初期化
    setEditTitle(task.title)
    setEditDescription(task.description || '')
    setEditStatus(task.status)
    if (task.start_date) {
      setEditStartDate(task.start_date.split('T')[0])
    } else {
      setEditStartDate('')
    }
    if (task.due_date) {
      setEditDueDate(task.due_date.split('T')[0])
      const timePart = task.due_date.includes('T') ? task.due_date.split('T')[1]?.substring(0, 5) : ''
      setEditDueTime(timePart && timePart !== '00:00' ? timePart : '')
    } else {
      setEditDueDate('')
      setEditDueTime('')
    }
    // 進捗を初期化
    setEditProgress(task.progress_percentage || 0)
    // 担当者を初期化
    setEditAssignedUserId(task.assigned_to_user_id || null)
    setEditAssignedUser(task.assigned_user || null)

    // 添付ファイルを読み込む
    const loadAttachments = async () => {
      setTaskImages([])
      setTaskFiles([])
      setLoadingAttachments(true)

      try {
        const [imagesResult, filesResult] = await Promise.all([
          getTodoImages(task.id),
          getTodoFiles(task.id)
        ])

        if (imagesResult.success && imagesResult.images) {
          const imagesWithUrls = await Promise.all(
            imagesResult.images.map(async (img) => ({
              ...img,
              thumbnailUrl: await getThumbnailUrl(img.image_url),
              optimizedUrl: await getOptimizedImageUrl(img.image_url)
            }))
          )
          setTaskImages(imagesWithUrls)
        }

        if (filesResult.success && filesResult.files) {
          setTaskFiles(filesResult.files)
        }
      } catch (error) {
        console.error('Failed to load attachments:', error)
      } finally {
        setLoadingAttachments(false)
      }
    }

    loadAttachments()
  }, [task])

  // Handle save task changes
  const handleSaveTask = async () => {
    if (!task || !editTitle.trim()) return

    setIsSaving(true)
    try {
      initializeFeedback()

      // Build due_date with time if provided
      let dueDate: string | null = null
      if (editDueDate) {
        if (editDueTime) {
          dueDate = `${editDueDate}T${editDueTime}:00`
        } else {
          dueDate = `${editDueDate}T00:00:00`
        }
      }

      // Build start_date
      let startDate: string | null = null
      if (editStartDate) {
        startDate = `${editStartDate}T00:00:00`
      }

      await updateTodo(task.id, {
        title: editTitle.trim(),
        description: editDescription.trim() || null,
        status: editStatus,
        start_date: startDate,
        due_date: dueDate,
        progress_percentage: editProgress,
        assigned_to_user_id: editAssignedUserId,
        assignment_status: editAssignedUserId ? 'assigned' : 'unassigned'
      })

      if (editStatus === 'completed' && task.status !== 'completed') {
        triggerFeedback('complete', { soundEnabled })
      } else {
        triggerFeedback('create', { soundEnabled })
      }

      onClose()
    } catch (error) {
      console.error('Failed to save task:', error)
      alert('タスクの保存に失敗しました')
    } finally {
      setIsSaving(false)
    }
  }

  // Handle status change
  const handleStatusChange = async (newStatus: Todo['status']) => {
    if (!task) return
    initializeFeedback()
    await updateTodo(task.id, { status: newStatus })

    if (newStatus === 'completed') {
      triggerFeedback('complete', { soundEnabled })
    } else if (newStatus === 'deleted') {
      triggerFeedback('delete', { soundEnabled })
    }
    onClose()
  }

  // Duplicate task
  const handleDuplicateTask = async () => {
    if (!task) return
    try {
      initializeFeedback()
      await addTodo({
        title: `${task.title} (コピー)`,
        description: task.description,
        priority: task.priority,
        status: 'pending',
        start_date: task.start_date,
        due_date: task.due_date,
        project_id: task.project_id,
        assigned_to_user_id: null,
        assignment_status: 'unassigned',
        progress_percentage: 0,
        deleted_at: null,
        thumbnail_image_id: null,
        thumbnail_size: 'small'
      })
      triggerFeedback('create', { soundEnabled })
      onClose()
    } catch (error) {
      console.error('Error duplicating task:', error)
      alert('タスクの複製に失敗しました')
    }
  }

  if (!task) return null

  const project = getProject(task.project_id)

  return (
    <>
      {createPortal(
        <div
          className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[9999]"
          onClick={onClose}
        >
          <div
            className="bg-white w-full sm:w-[480px] sm:rounded-lg rounded-t-2xl max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b bg-indigo-600">
              <h3 className="text-lg font-semibold text-white">タスク詳細</h3>
              <button
                onClick={onClose}
                className="p-1 hover:bg-indigo-500 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 space-y-4 overflow-y-auto max-h-[60vh]">
              {/* Status Dropdown & Project Badge */}
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value as Todo['status'])}
                  className={`px-2 py-0.5 text-xs rounded-full font-medium cursor-pointer ${getStatusColor(editStatus)}`}
                >
                  <option value="pending">未着手</option>
                  <option value="in_progress">進行中</option>
                  <option value="completed">完了</option>
                </select>

                {/* Project Badge */}
                {project && (
                  <span
                    className="flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded-full bg-white border border-gray-200"
                    style={{ color: project.color }}
                  >
                    {project.name}
                  </span>
                )}
              </div>

              {/* Task Title */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">タイトル</label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 task-title"
                  placeholder="タスクのタイトル"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">説明</label>
                <RichTextEditor
                  content={editDescription}
                  onChange={setEditDescription}
                  placeholder="タスクの説明（任意）"
                  className="task-description"
                />
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">開始日</label>
                  <input
                    ref={editStartDateRef}
                    type="date"
                    value={editStartDate}
                    onChange={(e) => setEditStartDate(e.target.value)}
                    onClick={() => editStartDateRef.current?.showPicker?.()}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 task-description"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">期限日</label>
                  <input
                    ref={editDueDateRef}
                    type="date"
                    value={editDueDate}
                    onChange={(e) => setEditDueDate(e.target.value)}
                    onClick={() => editDueDateRef.current?.showPicker?.()}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 task-description"
                  />
                </div>
              </div>

              {/* Due Time */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">期限時間（任意）</label>
                <input
                  ref={editDueTimeRef}
                  type="time"
                  value={editDueTime}
                  onChange={(e) => setEditDueTime(e.target.value)}
                  onClick={() => editDueTimeRef.current?.showPicker?.()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 task-description"
                />
              </div>

              {/* Team Project: Assignee and Progress */}
              {project?.is_team_project && project?.team_id && (
                <div className="space-y-3 p-3 bg-blue-50 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-800">チームタスク設定</h4>

                  {/* Assignee */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <UserPlus className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-gray-700">担当者</span>
                    </div>
                    <button
                      onClick={() => setShowAssignModal(true)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      {editAssignedUser ? (
                        <>
                          <div
                            className={`h-5 w-5 rounded-full bg-gradient-to-br ${getUserColorFromId(editAssignedUser.id)} flex items-center justify-center`}
                          >
                            {editAssignedUser.avatar_url ? (
                              <img
                                src={editAssignedUser.avatar_url}
                                alt={editAssignedUser.nickname || 'User'}
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              <span className="text-white text-[8px] font-semibold">
                                {getUserInitial(editAssignedUser.nickname, editAssignedUser.email)}
                              </span>
                            )}
                          </div>
                          <span className="text-sm text-gray-700">{editAssignedUser.nickname || '担当者'}</span>
                        </>
                      ) : (
                        <span className="text-sm text-gray-500">未割り当て</span>
                      )}
                    </button>
                  </div>

                  {/* Progress */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-gray-700">進捗</span>
                    </div>
                    <button
                      onClick={() => setShowProgressPanel(true)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      <span className="text-sm font-medium text-blue-600">{editProgress}%</span>
                      <span className="text-xs text-gray-500">更新</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Attached Images & Files */}
              {loadingAttachments ? (
                <div className="text-sm text-gray-500">添付ファイルを読み込み中...</div>
              ) : (
                <>
                  {taskImages.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Image className="w-4 h-4" />
                          <span>画像 ({taskImages.length})</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowImageModal(true)}
                          className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                          管理
                        </button>
                      </div>
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {taskImages.map((img, index) => (
                          <button
                            key={img.id}
                            type="button"
                            className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-lg"
                            onClick={(e) => {
                              e.stopPropagation()
                              setPreviewImageIndex(index)
                            }}
                          >
                            <img
                              src={img.thumbnailUrl || img.image_url}
                              alt={img.file_name}
                              className="w-16 h-16 object-cover rounded-lg border hover:opacity-80 transition-opacity"
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {taskFiles.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <File className="w-4 h-4" />
                          <span>ファイル ({taskFiles.length})</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowFileModal(true)}
                          className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                          管理
                        </button>
                      </div>
                      <div className="space-y-2">
                        {taskFiles.map(file => (
                          <div
                            key={file.id}
                            className="flex items-start gap-2 p-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                          >
                            <div className="flex-shrink-0 mt-0.5">
                              {getCategoryIcon(file.file_category)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium truncate">{file.file_name}</p>
                              <div className="flex items-center gap-1 text-[10px] text-gray-500 mt-0.5">
                                <span className="capitalize">{file.file_category}</span>
                                <span>•</span>
                                <span>{formatFileSize(file.file_size)}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {canPreview(file) && (
                                <button
                                  type="button"
                                  onClick={async (e) => {
                                    e.stopPropagation()
                                    const url = await getFileUrl(file.file_url)
                                    window.open(url, '_blank')
                                  }}
                                  className="p-1 text-green-600 hover:bg-green-50 rounded"
                                  title="プレビュー"
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  downloadFile(file.file_url, file.file_name)
                                }}
                                className="p-1 text-indigo-600 hover:bg-indigo-50 rounded"
                                title="ダウンロード"
                              >
                                <Download className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-2 sm:gap-[10px] px-4 py-3 border-t bg-gray-50">
              <button
                type="button"
                onClick={handleDuplicateTask}
                className="p-1.5 sm:p-2 hover:bg-gray-100 rounded"
                aria-label="タスクを複製"
              >
                <img src={ButtonL} alt="タスクを複製" className="h-6 w-6 sm:h-7 sm:w-7" />
              </button>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="p-1.5 sm:p-2 hover:bg-red-50 rounded"
                aria-label="ゴミ箱に移動"
              >
                <img src={ButtonF} alt="ゴミ箱に移動" className="h-6 w-6 sm:h-7 sm:w-7" />
              </button>
              <button
                type="button"
                onClick={handleSaveTask}
                disabled={isSaving || !editTitle.trim()}
                className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    保存中...
                  </>
                ) : (
                  '保存'
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Image Preview Modal */}
      {previewImageIndex !== null && taskImages.length > 0 && createPortal(
        <div
          className="fixed inset-0 z-[10000] bg-black bg-opacity-95"
          onClick={() => setPreviewImageIndex(null)}
        >
          {/* Close button */}
          <button
            onClick={() => setPreviewImageIndex(null)}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full z-20"
            aria-label="閉じる"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Navigation buttons */}
          {taskImages.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setPreviewImageIndex((prev) =>
                    prev !== null ? (prev - 1 + taskImages.length) % taskImages.length : 0
                  )
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full z-20"
                aria-label="前の画像"
              >
                <ChevronLeft className="h-8 w-8" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setPreviewImageIndex((prev) =>
                    prev !== null ? (prev + 1) % taskImages.length : 0
                  )
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full z-20"
                aria-label="次の画像"
              >
                <ChevronRight className="h-8 w-8" />
              </button>
            </>
          )}

          {/* Main image */}
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <img
              src={taskImages[previewImageIndex]?.optimizedUrl || taskImages[previewImageIndex]?.image_url}
              alt={taskImages[previewImageIndex]?.file_name}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Image counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black bg-opacity-50 text-white text-sm rounded-full">
            {previewImageIndex + 1} / {taskImages.length}
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="タスクをゴミ箱に移動"
        message="このタスクをゴミ箱に移動しますか？"
        confirmText="ゴミ箱に移動"
        cancelText="キャンセル"
        onConfirm={() => {
          handleStatusChange('deleted')
          setShowDeleteConfirm(false)
        }}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      {/* Image Management Modal */}
      {showImageModal && (
        <TodoImageModal
          todoId={task.id}
          todoTitle={task.title}
          isOpen={showImageModal}
          onClose={async () => {
            setShowImageModal(false)
            // Reload images after close
            const result = await getTodoImages(task.id)
            if (result.success && result.images) {
              const imagesWithUrls = await Promise.all(
                result.images.map(async (img) => ({
                  ...img,
                  thumbnailUrl: await getThumbnailUrl(img.image_url),
                  optimizedUrl: await getOptimizedImageUrl(img.image_url)
                }))
              )
              setTaskImages(imagesWithUrls)
            }
          }}
        />
      )}

      {/* File Management Modal */}
      {showFileModal && (
        <TodoFileModal
          todoId={task.id}
          todoTitle={task.title}
          isOpen={showFileModal}
          onClose={async () => {
            setShowFileModal(false)
            // Reload files after close
            const result = await getTodoFiles(task.id)
            if (result.success && result.files) {
              setTaskFiles(result.files)
            }
          }}
        />
      )}

      {/* Assign Task Modal */}
      {showAssignModal && project?.team_id && (
        <AssignTaskModal
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          todoTitle={task.title}
          teamId={project.team_id}
          currentAssignedUserId={editAssignedUserId}
          onSelect={(userId, userInfo) => {
            setEditAssignedUserId(userId)
            setEditAssignedUser(userInfo)
            setShowAssignModal(false)
          }}
        />
      )}

      {/* Progress Panel */}
      {showProgressPanel && project?.team_id && createPortal(
        <div
          className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50"
          onClick={() => setShowProgressPanel(false)}
        >
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm mx-4">
            <TaskProgressPanel
              currentProgress={editProgress}
              onProgressSelect={(progress: number) => {
                setEditProgress(progress)
                setShowProgressPanel(false)
              }}
              onClose={() => setShowProgressPanel(false)}
            />
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
